import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { subscriptionsAPI, walletAPI, productsAPI, addressesAPI } from '../lib/api';
import {
  Play,
  Pause,
  Plus,
  RefreshCw,
  Calendar,
  PiggyBank,
  Bell,
  Package,
  Search,
  X,
  Clock,
  MapPin,
  ChevronDown,
  Check,
  SkipForward,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Subscription {
  id: string;
  _id: string;
  productId: string;
  name?: string;
  productName: string;
  quantity: number;
  frequency: string;
  status: string;
  nextDelivery?: string;
  startDate: string;
  price?: number;
}

interface SubscriptionStats {
  active: number;
  paused: number;
  totalSavings: number;
  upcoming: number;
  deliveries: number;
  walletBalance: number;
}

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<SubscriptionStats>({
    active: 0,
    paused: 0,
    totalSavings: 0,
    upcoming: 0,
    deliveries: 0,
    walletBalance: 0,
  });
  const [upcomingDeliveries, setUpcomingDeliveries] = useState<any[]>([]);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [userAddresses, setUserAddresses] = useState<any[]>([]);
  const [selectedProductsForSub, setSelectedProductsForSub] = useState<string[]>([]);
  const [subscriptionForm, setSubscriptionForm] = useState({
    name: '',
    frequency: 'daily',
    deliveryTime: '6:00 AM - 7:00 AM (Early Bird)',
    deliveryAddress: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchProductsAndAddresses();
  }, []);

  useEffect(() => {
    filterSubscriptions();
  }, [subscriptions, activeFilter, searchTerm]);

  const fetchProductsAndAddresses = async () => {
    try {
      const [productsRes, addressesRes] = await Promise.all([
        productsAPI.getAll(),
        addressesAPI.getAll()
      ]);
      setProducts(productsRes.data || []);
      setUserAddresses(addressesRes.data || []);
      if (addressesRes.data?.length > 0) {
        const defaultAddr = addressesRes.data.find((a: any) => a.isDefault) || addressesRes.data[0];
        setSubscriptionForm(prev => ({ ...prev, deliveryAddress: defaultAddr._id }));
      }
    } catch (error) {
      console.error('Error fetching products/addresses:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [subsRes, walletRes, upcomingRes] = await Promise.all([
        subscriptionsAPI.getAll().catch(() => ({ data: [] })),
        walletAPI.getBalance().catch(() => ({ data: { balance: 2200 } })),
        subscriptionsAPI.getUpcoming().catch(() => ({ data: [] })),
      ]);

      const subs = subsRes.data || [];
      setSubscriptions(subs);
      setUpcomingDeliveries(upcomingRes.data || []);

      const statsData = await subscriptionsAPI.getStats().catch(() => ({ data: {} }));
      setStats({
        active: statsData.data?.active || subs.filter((s: Subscription) => s.status === 'ACTIVE' || s.status === 'active').length,
        paused: statsData.data?.paused || subs.filter((s: Subscription) => s.status === 'PAUSED' || s.status === 'paused').length,
        totalSavings: statsData.data?.totalSavings || 0,
        upcoming: upcomingRes.data?.length || 0,
        deliveries: statsData.data?.upcomingDeliveries || 0,
        walletBalance: walletRes.data?.balance || 0,
      });
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSubscriptions = () => {
    let filtered = subscriptions;

    if (activeFilter !== 'all') {
      filtered = subscriptions.filter((s) => {
        const statusUpper = s.status?.toUpperCase();
        return statusUpper === activeFilter.toUpperCase();
      });
    }

    if (searchTerm) {
      filtered = filtered.filter((s) =>
        s.productName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSubscriptions(filtered);
  };

  const handlePauseResume = async (id: string, currentStatus: string) => {
    try {
      const normalizedStatus = currentStatus.toUpperCase();
      const newStatus = normalizedStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await subscriptionsAPI.updateStatus(id, newStatus);
      toast.success(`Subscription ${newStatus === 'ACTIVE' ? 'resumed' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleSkipNextDelivery = async (id: string) => {
    try {
      await subscriptionsAPI.skipNextDelivery(id);
      toast.success('Next delivery skipped successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to skip delivery');
    }
  };

  const handleCreateSubscription = async () => {
    if (selectedProductsForSub.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    if (!subscriptionForm.deliveryAddress) {
      toast.error('Please select a delivery address');
      return;
    }

    try {
      const items = selectedProductsForSub.map(id => {
        const p = products.find(prod => (prod.id || prod._id) === id);
        return {
          productId: id,
          name: p?.name,
          quantity: 1,
          unit: p?.unit
        };
      });

      await subscriptionsAPI.create({
        ...subscriptionForm,
        items,
        status: 'active',
        startDate: new Date(),
      });

      toast.success('Subscription created successfully');
      setIsSubscriptionModalOpen(false);
      setSelectedProductsForSub([]);
      setSubscriptionForm({
        name: '',
        frequency: 'daily',
        deliveryTime: '6:00 AM - 7:00 AM (Early Bird)',
        deliveryAddress: userAddresses.find(a => a.isDefault)?._id || userAddresses[0]?._id || '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create subscription');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-gray-500 mt-1">Manage your automated deliveries and save more on daily essentials</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm text-gray-600">
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setIsSubscriptionModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#10b981] text-white rounded-xl hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20 font-bold"
            >
              <Plus size={20} />
              New Subscription
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="responsive-stats-grid">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-5 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Play size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Active</span>
            </div>
            <p className="text-3xl font-black">{stats.active}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Active Plans</p>
          </div>

          <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl p-5 text-white shadow-lg shadow-orange-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Pause size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Paused</span>
            </div>
            <p className="text-3xl font-black">{stats.paused}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Paused Plans</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-3xl p-5 text-white shadow-lg shadow-emerald-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <PiggyBank size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Savings</span>
            </div>
            <p className="text-3xl font-black">₹{stats.totalSavings}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Total Savings</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl p-5 text-white shadow-lg shadow-purple-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Calendar size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">7 Days</span>
            </div>
            <p className="text-3xl font-black">{stats.upcoming}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Upcoming</p>
          </div>
        </div>

        {/* Upcoming Deliveries */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
          <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-6">
            <Bell className="text-[#10b981]" size={20} />
            Upcoming Deliveries
          </h3>
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
            {upcomingDeliveries.length > 0 ? (
              upcomingDeliveries.map((delivery, idx) => {
                const deliveryDate = delivery.nextDeliveryDate ? new Date(delivery.nextDeliveryDate) : new Date();
                const daysUntil = Math.ceil((deliveryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                const product = delivery.productId || delivery.product;
                return (
                  <div key={idx} className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4 min-w-[280px] border border-gray-100 hover:bg-white hover:shadow-md transition-all group">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                      <Package className="text-orange-500" size={24} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 truncate">{product?.name || delivery.productName || 'Product'}</p>
                      <p className="text-xs text-gray-500 font-medium">{delivery.quantity} units • {delivery.frequency || 'Daily'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-xs font-bold uppercase tracking-wider ${daysUntil <= 1 ? 'text-red-500' : 'text-[#10b981]'}`}>
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                      </p>
                      <p className="text-[10px] text-gray-400 font-bold mt-0.5">{deliveryDate.toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center w-full py-8 text-gray-400">
                <Package className="mb-2 opacity-20" size={48} />
                <p className="text-sm font-medium">No deliveries scheduled for the next 7 days</p>
              </div>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search subscriptions by product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#10b981] focus:border-transparent outline-none transition-all shadow-sm"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 no-scrollbar">
            {[
              { key: 'all', label: 'All', count: subscriptions.length },
              { key: 'active', label: 'Active', count: stats.active },
              { key: 'paused', label: 'Paused', count: stats.paused },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-3 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${activeFilter === filter.key
                  ? 'bg-[#1e3a5f] text-white shadow-lg shadow-blue-900/20'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
              >
                {filter.label} <span className={`ml-1.5 ${activeFilter === filter.key ? 'text-white/60' : 'text-gray-400'}`}>{filter.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Subscriptions List */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          {filteredSubscriptions.length > 0 ? (
            <div className="divide-y divide-gray-50">
              {filteredSubscriptions.map((sub) => (
                <div key={sub.id || sub._id} className="p-5 sm:p-6 hover:bg-gray-50/50 transition-colors group">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform border border-gray-100">
                        <Package className="text-[#10b981]" size={28} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-900 text-lg truncate group-hover:text-[#10b981] transition-colors">{sub.name || sub.productName}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{sub.quantity} units</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full" />
                          <span className="text-xs font-bold text-[#10b981] uppercase tracking-widest">{sub.frequency}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6">
                      <div className="text-right hide-on-mobile">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Next Delivery</p>
                        <p className="text-sm font-bold text-gray-900">{sub.nextDelivery ? new Date(sub.nextDelivery).toLocaleDateString() : 'Not scheduled'}</p>
                      </div>
                      <span
                        className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${(sub.status === 'active' || sub.status === 'ACTIVE')
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                          }`}
                      >
                        {sub.status}
                      </span>

                      <div className="flex gap-2">
                        {(sub.status === 'active' || sub.status === 'ACTIVE') && (
                          <button
                            onClick={() => handleSkipNextDelivery(sub.id || sub._id)}
                            className="bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white px-3 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2"
                            title="Skip Next Delivery"
                          >
                            <SkipForward size={18} /> <span className="hidden sm:inline">Skip Next</span>
                          </button>
                        )}

                        <button
                          onClick={() => handlePauseResume(sub.id || sub._id, sub.status)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${(sub.status === 'active' || sub.status === 'ACTIVE')
                            ? 'bg-orange-50 text-orange-600 hover:bg-orange-600 hover:text-white'
                            : 'bg-green-50 text-green-600 hover:bg-green-600 hover:text-white'
                            }`}
                        >
                          {(sub.status === 'active' || sub.status === 'ACTIVE') ? (
                            <>
                              <Pause size={18} /> <span className="hidden sm:inline">Pause</span>
                            </>
                          ) : (
                            <>
                              <Play size={18} /> <span className="hidden sm:inline">Resume</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="text-gray-200" size={48} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No subscriptions found</h3>
              <p className="text-gray-500 max-w-xs mx-auto mb-8">Start your first subscription to enjoy hassle-free daily deliveries</p>
              <button
                onClick={() => navigate('/products')}
                className="px-8 py-3 bg-[#10b981] text-white rounded-2xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20"
              >
                Browse Products
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Subscription Modal */}
      {isSubscriptionModalOpen && (
        <div className="modal-overlay backdrop-blur-sm">
          <div className="modal-content max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Create New Subscription</h2>
              <button onClick={() => setIsSubscriptionModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Subscription Name</label>
                <input
                  type="text"
                  placeholder="e.g., Morning Milk & Bread"
                  value={subscriptionForm.name}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Select Products</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-1 custom-scrollbar">
                  {products.map((product) => {
                    const isSelected = selectedProductsForSub.includes(product.id || product._id);
                    return (
                      <button
                        key={product.id || product._id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedProductsForSub(selectedProductsForSub.filter(id => id !== (product.id || product._id)));
                          } else {
                            setSelectedProductsForSub([...selectedProductsForSub, (product.id || product._id)]);
                          }
                        }}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition-all text-left ${isSelected
                          ? 'border-[#10b981] bg-green-50/50 ring-1 ring-[#10b981]'
                          : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-[#10b981]'
                          }`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-white' : 'bg-white shadow-sm'}`}>
                          <Package size={24} className={isSelected ? 'text-[#10b981]' : 'text-gray-300'} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-gray-900 truncate">{product.name}</p>
                          <p className="text-xs font-bold text-[#10b981]">₹{product.price.toFixed(2)}</p>
                        </div>
                        {isSelected && <div className="w-5 h-5 bg-[#10b981] rounded-full flex items-center justify-center shrink-0"><Check size={12} className="text-white" /></div>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Delivery Frequency</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: 'daily', label: 'Daily', desc: 'Every day' },
                    { id: 'alternate', label: 'Alternate', desc: 'Every 2nd day' },
                    { id: 'weekly', label: 'Weekly', desc: 'Once a week' },
                  ].map((freq) => (
                    <button
                      key={freq.id}
                      onClick={() => setSubscriptionForm({ ...subscriptionForm, frequency: freq.id })}
                      className={`p-4 rounded-xl border transition-all text-left ${subscriptionForm.frequency === freq.id
                        ? 'border-[#10b981] bg-green-50/50 ring-1 ring-[#10b981]'
                        : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-[#10b981]'
                        }`}
                    >
                      <p className="font-bold text-gray-900">{freq.label}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase mt-1">{freq.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Delivery Time</label>
                  <div className="relative">
                    <Clock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      value={subscriptionForm.deliveryTime}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, deliveryTime: e.target.value })}
                      className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none appearance-none transition-all"
                    >
                      <option>6:00 AM - 7:00 AM</option>
                      <option>7:00 AM - 8:00 AM</option>
                      <option>8:00 AM - 9:00 AM</option>
                    </select>
                    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Delivery Address</label>
                  <div className="relative">
                    <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      value={subscriptionForm.deliveryAddress}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, deliveryAddress: e.target.value })}
                      className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none appearance-none transition-all"
                    >
                      {userAddresses.length > 0 ? (
                        userAddresses.map(addr => (
                          <option key={addr._id} value={addr._id}>
                            {addr.type.toUpperCase()}: {addr.fullAddress.substring(0, 20)}...
                          </option>
                        ))
                      ) : (
                        <option value="">No addresses found</option>
                      )}
                    </select>
                    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
              <button
                onClick={() => setIsSubscriptionModalOpen(false)}
                className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubscription}
                className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20"
              >
                Start Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Subscriptions;